import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { useLanguage, dateLocale } from './i18n';
import './NotificationPanel.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const T = {
  kk: {
    title: 'Хабарламалар',
    loading: 'Хабарламалар жүктелуде...',
    empty: 'Сізде хабарламалар жоқ',
    loadError: 'Хабарламаларды жүктеу қатесі'
  },
  ru: {
    title: 'Уведомления',
    loading: 'Загрузка уведомлений...',
    empty: 'У вас нет уведомлений',
    loadError: 'Ошибка загрузки уведомлений'
  }
};

const NotificationPanel = ({ onClose, onEventSelect }) => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const t = T[language];
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadNotifications();
  }, []);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/notifications`);
      setNotifications(response.data);
    } catch (err) {
      console.error('Error loading notifications:', err);
      setError(t.loadError);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await axios.put(`${API}/notifications/${notificationId}/read`, {});
      
      // Update local state
      setNotifications(prev => 
        prev.map(notif => 
          notif.id === notificationId 
            ? { ...notif, is_read: true }
            : notif
        )
      );
    } catch (err) {
      console.error('Error marking notification as read:', err);
    }
  };

  const handleNotificationClick = async (notification) => {
    // Mark as read if unread
    if (!notification.is_read) {
      await markAsRead(notification.id);
    }
    
    // Open event details if event_id exists
    if (notification.event_id && onEventSelect) {
      onEventSelect(notification.event_id);
      onClose(); // Close notification panel
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'new_event': return '📢';
      case 'status_change': return '🔄';
      case 'comment': return '💬';
      default: return '📋';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString(dateLocale(language), {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="notification-panel-overlay">
        <div className="notification-panel">
          <div className="notification-header">
            <h3>{t.title}</h3>
            <button className="close-btn" onClick={onClose}>✕</button>
          </div>
          <div className="notification-content">
            <div className="loading-spinner"></div>
            <p>{t.loading}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="notification-panel-overlay">
      <div className="notification-panel">
        <div className="notification-header">
          <h3>{t.title}</h3>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>
        
        <div className="notification-content">
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}
          
          {notifications.length === 0 ? (
            <div className="empty-notifications">
              <p>{t.empty}</p>
            </div>
          ) : (
            <div className="notifications-list">
              {notifications.map((notification) => (
                <div 
                  key={notification.id}
                  className={`notification-item ${!notification.is_read ? 'unread' : ''} clickable`}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div className="notification-icon">
                    {getNotificationIcon(notification.notification_type)}
                  </div>
                  <div className="notification-details">
                    <div className="notification-title">
                      {notification.title}
                    </div>
                    <div className="notification-message">
                      {notification.message}
                    </div>
                    <div className="notification-time">
                      {formatDate(notification.created_at)}
                    </div>
                  </div>
                  {!notification.is_read && (
                    <div className="unread-indicator"></div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationPanel;